import type {
  Message,
  MessageCreateRequest,
  MessageListResponse
} from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { reportError } from '@/domains/observability'

import { sendMessage } from './api'
import { conversationMessagesKey, CONVERSATIONS_LIST_KEY } from './keys'

/** optimistic send: append a placeholder message to the thread cache before
 * the request resolves so the sender feels instant typing regardless of
 * RTT. on success we replace by clientId (server echoes it back, but the
 * socket broadcast does the heavy lifting . see socket-handlers.ts dedup
 * logic). on error we roll back + surface a toast at the call site.
 *
 * the optimistic row's id is the clientId . we generate one per send so
 * the socket broadcast can match-and-skip it.
 */
export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient()
  const messagesKey = conversationMessagesKey(conversationId)

  /** mutation variables extend MessageCreateRequest with sender display
   * fields. mutationFn only forwards the wire request to the server;
   * the sender fields are consumed by onMutate to build the optimistic
   * row before the response lands.
   */
  type Variables = MessageCreateRequest & {
    senderUserId: string
    senderUserName: string | null
    senderCompanyId: string | null
    senderCompanyName: string | null
  }

  return useMutation({
    mutationFn: ({ body, clientId, attachments }: Variables) =>
      sendMessage(conversationId, { body, clientId, attachments }),
    onMutate: async vars => {
      await queryClient.cancelQueries({ queryKey: messagesKey })
      const previous = queryClient.getQueryData<{
        pages: MessageListResponse[]
        pageParams: unknown[]
      }>(messagesKey)

      const optimistic: Message = {
        id: vars.clientId!,
        conversationId,
        senderUserId: vars.senderUserId,
        senderUserName: vars.senderUserName,
        senderCompanyId: vars.senderCompanyId,
        senderCompanyName: vars.senderCompanyName,
        body: vars.body,
        /** optimistic row carries no attachments. the server response
         * replaces this row with the real one (including the signed
         * attachment urls), so the user sees their files appear when
         * the request completes.
         */
        attachments: [],
        createdAt: new Date().toISOString(),
        deletedAt: null,
        deletedByUserName: null
      }

      queryClient.setQueryData(messagesKey, old => {
        const cache = (old as
          | { pages: MessageListResponse[]; pageParams: unknown[] }
          | undefined) ?? {
          pages: [{ items: [], nextCursor: null }],
          pageParams: [undefined]
        }
        const [firstPage, ...rest] = cache.pages
        const updatedFirst: MessageListResponse = {
          items: [optimistic, ...(firstPage?.items ?? [])],
          nextCursor: firstPage?.nextCursor ?? null
        }
        return { ...cache, pages: [updatedFirst, ...rest] }
      })

      return { previous }
    },
    onSuccess: (real, vars) => {
      /** replace the optimistic row (id === clientId) with the real one.
       * the socket broadcast also fires. it'll dedup by id since they
       * now match.
       */
      queryClient.setQueryData<
        | {
            pages: MessageListResponse[]
            pageParams: unknown[]
          }
        | undefined
      >(messagesKey, old => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            items: page.items.map(message =>
              message.id === vars.clientId ? real : message
            )
          }))
        }
      })

      /** also nudge the conversation list so the sender's own list row
       * shows the new lastMessage preview. the server broadcasts a
       * messaging:message event over socket.to(room) which excludes the
       * originating socket, so the sender's tab never receives it.
       * invalidating the list key forces a refetch on next mount; cheap
       * vs. trying to patch every cached search variant by hand.
       */
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_LIST_KEY })
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(messagesKey, ctx.previous)
      }
      /** the optimistic row vanishes from the UI when we roll back, so
       * the failure is invisible to the React error boundary. report
       * to Sentry directly so we can see it.
       */
      reportError(err)
    }
  })
}
